import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

export type UserType = 'Member' | 'Supplier' | 'FreightForwarder' | 'QCInspector';
export type MemberRoleType = 'SST' | 'AM' | 'Supplier' | 'QC' | 'Client' | 'FreightForwarder' | 'QCInspector';

interface User {
  memberNo: number;
  email: string;
  firstName: string;
  lastName: string;
  roleTypeId?: number;
  isActive: boolean;
  userType?: UserType;
  roleType?: MemberRoleType;
  
  // Member role specific properties
  memberRole?: MemberRoleType;
  roleDisplayName?: string;
  isAdmin?: boolean;
  canAccessReports?: boolean;
  canManageUsers?: boolean;
  canManageSuppliers?: boolean;
}

interface LoginRequest {
  email: string;
  password: string;
  rememberMe?: boolean;
}

interface LoginResponse {
  token: string;
  memberNo: number;
  email: string;
  firstName: string;
  lastName: string;
  roleTypeId?: number;
  userType: number; // UserType enum from backend
  expiresAt: string;
  isActive: boolean;
  
  // Member role specific properties from backend
  memberRole?: number; // MemberRoleType enum from backend
  roleDisplayName?: string;
  isAdmin?: boolean;
  canAccessReports?: boolean;
  canManageUsers?: boolean;
  canManageSuppliers?: boolean;
}

export interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (credentials: LoginRequest) => Promise<void>;
  logout: () => void;
  token: string | null;
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Initialize auth state from localStorage on app start
  useEffect(() => {
    const initializeAuth = () => {
      try {
        const storedToken = localStorage.getItem('auth_token');
        const storedUser = localStorage.getItem('auth_user');
        
        if (storedToken && storedUser) {
          const parsedUser = JSON.parse(storedUser);
          
          // Check if token is expired
          const expiryTime = localStorage.getItem('auth_expiry');
          if (expiryTime && new Date(expiryTime) > new Date()) {
            setToken(storedToken);
            setUser(parsedUser);
          } else {
            // Token expired, clear storage
            localStorage.removeItem('auth_token');
            localStorage.removeItem('auth_user');
            localStorage.removeItem('auth_expiry');
          }
        }
      } catch (error) {
        console.error('Error initializing auth:', error);
        // Clear potentially corrupted data
        localStorage.removeItem('auth_token');
        localStorage.removeItem('auth_user');
        localStorage.removeItem('auth_expiry');
      } finally {
        setIsLoading(false);
      }
    };

    initializeAuth();
  }, []);

  const login = async (credentials: LoginRequest): Promise<void> => {
    try {
      setIsLoading(true);
      
      const response = await fetch('/api/auth/login', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(credentials),
      });

      if (!response.ok) {
        let errorMessage = 'Login failed';
        try {
          const error = await response.json();
          errorMessage = error.message || errorMessage;
        } catch {
          // If we can't parse the error response, use the default message
          errorMessage = `Login failed with status ${response.status}`;
        }
        throw new Error(errorMessage);
      }

      // Check if response has content
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        throw new Error('API returned invalid response. Please check if the backend server is running.');
      }

      const responseText = await response.text();
      if (!responseText.trim()) {
        throw new Error('API returned empty response. Please check if the authentication endpoint is available.');
      }

      let loginData: LoginResponse;
      try {
        loginData = JSON.parse(responseText);
      } catch {
        throw new Error('API returned invalid JSON response. Please check the backend server.');
      }
      
      // Create user object from login response
      const userData: User = {
        memberNo: loginData.memberNo,
        email: loginData.email,
        firstName: loginData.firstName,
        lastName: loginData.lastName,
        roleTypeId: loginData.roleTypeId,
        isActive: loginData.isActive,
        userType: mapBackendUserType(loginData.userType),
        roleType: getRoleTypeFromId(loginData.roleTypeId),
        
        // Member role specific properties
        memberRole: mapBackendMemberRole(loginData.memberRole),
        roleDisplayName: loginData.roleDisplayName,
        isAdmin: loginData.isAdmin ?? false,
        canAccessReports: loginData.canAccessReports ?? true,
        canManageUsers: loginData.canManageUsers ?? false,
        canManageSuppliers: loginData.canManageSuppliers ?? false
      };

      // Store auth data
      localStorage.setItem('auth_token', loginData.token);
      localStorage.setItem('auth_user', JSON.stringify(userData));
      localStorage.setItem('auth_expiry', loginData.expiresAt);
      
      setToken(loginData.token);
      setUser(userData);
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const logout = () => {
    // Clear auth data
    localStorage.removeItem('auth_token');
    localStorage.removeItem('auth_user');
    localStorage.removeItem('auth_expiry');
    
    setToken(null);
    setUser(null);
    
    // Optional: Call logout endpoint
    fetch('/api/auth/logout', { method: 'POST' }).catch(() => {
      // Ignore errors on logout endpoint
    });
  };

  // Helper function to map backend UserType enum to frontend UserType
  const mapBackendUserType = (backendUserType: number): UserType => {
    // Backend UserType enum: Member = 1, Supplier = 2, FreightForwarder = 3, QCInspector = 4
    switch (backendUserType) {
      case 1: return 'Member';
      case 2: return 'Supplier';
      case 3: return 'FreightForwarder';
      case 4: return 'QCInspector';
      default: return 'Member'; // Default fallback
    }
  };

  // Helper function to map roleTypeId to MemberRoleType (for backward compatibility)
  const getRoleTypeFromId = (roleTypeId?: number): MemberRoleType | undefined => {
    // Updated mappings: SST = 1, AM = 2, Supplier = 3, QC = 4, Client = 5, FreightForwarder = 6
    switch (roleTypeId) {
      case 1: return 'SST';
      case 2: return 'AM';
      case 3: return 'Supplier';
      case 4: return 'QC';
      case 5: return 'Client';
      case 6: return 'FreightForwarder';
      default: return undefined;
    }
  };

  // Helper function to map backend MemberRoleType enum to frontend MemberRoleType
  const mapBackendMemberRole = (backendMemberRole?: number): MemberRoleType | undefined => {
    // Backend MemberRoleType enum: SST = 1, AM = 2, Supplier = 3, QC = 4, Client = 5, FreightForwarder = 6
    switch (backendMemberRole) {
      case 1: return 'SST';
      case 2: return 'AM';
      case 3: return 'Supplier';
      case 4: return 'QC';
      case 5: return 'Client';
      case 6: return 'FreightForwarder';
      default: return undefined;
    }
  };

  const contextValue: AuthContextType = {
    user,
    isAuthenticated: !!user && !!token,
    isLoading,
    login,
    logout,
    token
  };

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Helper hook to get current user's MemberNo
export const useMemberNo = (): number | null => {
  const { user } = useAuth();
  return user?.memberNo || null;
};

// Helper function to get stored token (for API calls)
export const getAuthToken = (): string | null => {
  return localStorage.getItem('auth_token');
};

// Helper function to get current user (for API calls)
export const getCurrentUser = (): User | null => {
  try {
    const storedUser = localStorage.getItem('auth_user');
    return storedUser ? JSON.parse(storedUser) : null;
  } catch {
    return null;
  }
};

// Role-based permission hooks
export const useIsMember = (): boolean => {
  const { user } = useAuth();
  return user?.userType === 'Member';
};

export const useIsSST = (): boolean => {
  const { user } = useAuth();
  return user?.userType === 'Member' && user?.memberRole === 'SST';
};

export const useIsAM = (): boolean => {
  const { user } = useAuth();
  return user?.userType === 'Member' && user?.memberRole === 'AM';
};

export const useCanAccessReports = (): boolean => {
  const { user } = useAuth();
  return user?.canAccessReports ?? false;
};

export const useCanManageUsers = (): boolean => {
  const { user } = useAuth();
  return user?.canManageUsers ?? false;
};

export const useCanManageSuppliers = (): boolean => {
  const { user } = useAuth();
  return user?.canManageSuppliers ?? false;
};

export const useIsAdmin = (): boolean => {
  const { user } = useAuth();
  return user?.isAdmin ?? false;
};
